import React, { useEffect, useState } from 'react';
import { DirectUpload } from 'activestorage';
import { filesize } from 'filesize';
import {
  Card,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  IconButton,
  ListItem as ListItemMui,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import Delete from '@mui/icons-material/Delete';

import appsignal from '../../../../../Services/ErrorReportingService';
import VirusScanStatusIcon from '../../../Shared/VirusScanStatusIcon';
import useVirusScanStatus from '../../../../../Hooks/useVirusScanStatus';
import { cloudinaryImageUrl } from '../../../../../Services/Cloudinary';

interface ListItemProps {
  name?: string;
  file: File | any;
  onUpload: (file: File) => void;
  onCancel: (fileId: string) => void;
  setDisabledForm: (isDisabled: boolean) => void;
  layout?: 'list' | 'image';
}

function ListItem({
  name,
  file,
  onUpload,
  onCancel,
  setDisabledForm,
  layout = 'list',
}: ListItemProps) {
  const [uploaded, setUploaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { status } = useVirusScanStatus({
    setDisabledForm,
    name,
    blobKey: file?.blob_key,
    onSuccess: (newstatus: any) => {
      onUpload({ ...file, virus_scan_status: newstatus });
    },
  });

  const uploadFile = (rawFile: any) => {
    // ignore already uploaded files
    if (rawFile.url) return;

    setUploading(true);
    setDisabledForm(true);

    // Use active storage direct upload lib to get a presigned blob id and attach it to form data
    const upload = new DirectUpload(rawFile, '/api/direct_uploads');

    upload.create((error: any, blob: any) => {
      if (error) {
        setErrored(true);
        setUploading(false);
        setDisabledForm(false);
        appsignal.sendError(error);
      } else {
        setUploaded(true);
        setUploading(false);
        setDisabledForm(false);
        setErrored(false);
        onUpload(blob);
      }
    });
  };

  useEffect(() => {
    if (!uploading && !uploaded && !errored) {
      uploadFile(file);
    }
  }, [uploading, uploaded, uploadFile]);

  const text = (
    <Typography color={errored ? 'error' : 'inherit'} component="span">
      <strong>
        {errored && <FormattedMessage id="ATTACHMENTS.ERROR.TITLE" />}
        {file.name || file.title}
      </strong>
    </Typography>
  );
  const fileSize = (
    <Typography variant="body2" component="span">
      {filesize(file.size)}
    </Typography>
  );

  const statusBlock = (status ||
    (file.virus_scan_status && file.virus_scan_status !== 'no_scan')) && (
    <Typography variant="body2" component="span" sx={{ position: 'relative', top: 3 }}>
      <VirusScanStatusIcon status={status || file.virus_scan_status} />
    </Typography>
  );

  if (layout === 'list') {
    return (
      <ListItemMui>
        <ListItemText
          primary={
            <>
              {text}
              {' — '}

              {fileSize}
              {' — '}

              {statusBlock}
            </>
          }
        />
        <ListItemSecondaryAction>
          {uploading && <CircularProgress size={15} />}

          {(!uploading || errored) && (
            <IconButton edge="end" aria-label="delete" onClick={() => onCancel(file)} size="large">
              <Delete color="error" />
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItemMui>
    );
  }

  return (
    <Card key={file?.title} sx={{ width: 200 }}>
      <CardMedia height={200} component="img" image={cloudinaryImageUrl(file.s3_key)} />
      <CardContent sx={{ pb: 0 }}>
        <div> {fileSize} </div>
        <div> {statusBlock} </div>
      </CardContent>
      <CardActions>
        {uploading && <CircularProgress size={15} />}

        {(!uploading || errored) && (
          <IconButton edge="end" aria-label="delete" onClick={() => onCancel(file)} size="large">
            <Delete color="error" />
          </IconButton>
        )}
      </CardActions>
    </Card>
  );
}

export default ListItem;
